import {
  seekSecondsFromVideoJsWallTs,
  wallTsForVideoJsPlayhead,
} from '@app/core/video-player/video-js/hls-playback-fix/hls-pdt-playback';
import { MediaPlayer, PlayerEvent } from '@mi-front/core';
import debugFn from 'debug';
import EventEmitter from 'events';
import Player from 'video.js/dist/types/player';

/**
 * Adapter that wraps a video.js player to implement the MediaPlayer interface,
 * enabling its use with the playback system.
 *
 * **HLS wall clock vs linear `baseTs`**
 *
 * When hls.js is attached (`player.hls`), seeks and `TS_UPDATE` use
 * {@link seekSecondsFromVideoJsWallTs} / {@link wallTsForVideoJsPlayhead}
 * so the playhead matches `#EXT-X-PROGRAM-DATE-TIME` + fragment `duration` from
 * the loaded manifest. That keeps UI time aligned with the playlist’s media
 * timeline when cumulative segment lengths differ from a naive
 * `baseTs + currentTime` line (e.g. wrong `EXTINF` vs mux, gaps). If HLS is
 * unavailable or PDT cannot resolve the position, we fall back to `baseTs` and
 * the caller-supplied seek `time` as before.
 */
export class VideoJsMediaPlayer extends EventEmitter implements MediaPlayer {
  private debug: debugFn.Debugger;

  constructor(
    private videoJsPlayer: Player,
    private config: { baseTs: number; playerId?: string },
  ) {
    super();

    this.debug = debugFn(`VideoMediaPlayer-${config.playerId}`);

    this.debug(
      'initialize videojs adapter with baseTs=%s',
      this.config.baseTs ? new Date(this.config.baseTs).toISOString() : null,
    );

    this.videoJsPlayer.on('timeupdate', () => {
      const ct = this.videoJsPlayer.currentTime() ?? 0;
      // Previously: `Math.round(this.config.baseTs + ct * 1000)` only — assumes media
      // seconds and wall time stay in strict lockstep. With HLS + PDT we instead use the
      // manifest’s program dates when hls.js is present; the helper falls back to that
      // same linear formula when there is no `.hls` or PDT cannot resolve `ct`.
      const ts = wallTsForVideoJsPlayhead(this.videoJsPlayer, ct, this.config.baseTs);
      this.emit(PlayerEvent.TS_UPDATE, ts);
    });

    this.videoJsPlayer.on('error', (error: Error) => {
      this.debug('error: %s', error.message);
      this.emit(PlayerEvent.ERROR, error);
    });

    this.videoJsPlayer.on('seeking', () => {
      this.emit(PlayerEvent.SEEKING_START);
    });

    this.videoJsPlayer.on('seeked', () => {
      this.emit(PlayerEvent.SEEKING_END);
    });

    this.videoJsPlayer.on('playing', () => {
      this.emit(PlayerEvent.PLAYING);
    });

    this.videoJsPlayer.on('pause', () => {
      this.emit(PlayerEvent.PAUSED);
    });
  }

  play() {
    if (!this.videoJsPlayer.paused()) {
      return;
    }

    this.debug('play');
    this.videoJsPlayer.play()?.catch((error) => {
      console.log(error);
    });
  }

  pause(): void {
    if (this.videoJsPlayer.paused()) {
      return;
    }

    this.debug('pause');
    this.videoJsPlayer.pause();
  }

  seek(args: { time: number; ts: number; playAfterSeek?: boolean }): void {
    this.debug(
      'seek to %.2fs / ts=%s (play after = %s)',
      args.time,
      new Date(args.ts).toISOString(),
      args.playAfterSeek ? 'yes' : 'no',
    );

    const seekToSeconds = seekSecondsFromVideoJsWallTs(this.videoJsPlayer, args.ts, args.time);
    this.videoJsPlayer.currentTime(seekToSeconds);

    if (args.playAfterSeek) {
      this.play();
    }
  }

  mute(): void {
    this.debug('mute');
    this.videoJsPlayer.muted(true);
  }

  unmute(): void {
    this.debug('unmute');
    this.videoJsPlayer.muted(false);
  }

  isMuted(): boolean {
    return this.videoJsPlayer.muted() ?? false;
  }

  dispose(): void {
    this.debug('dispose');

    if (this.videoJsPlayer.isDisposed()) {
      return;
    }

    this.videoJsPlayer.dispose();
  }
}
